import { getCookie } from "./getCookie";
import { NamedError } from "./NamedError";

/**
 * Adapted from https://microsoft.visualstudio.com/DefaultCollection/Xbox.Apps/_git/Xbox.Apps.Mercury?path=/shared/telemetry/correlationVector.ts
 */

const BASE_LENGTH = 22;
const MAX_LENGTH = 127;

export class CorrelationVector {
  private cV: string | undefined;
  private currentElement = 0;

  /**
   * Sets the app's current CV to a new value.
   * @param newCV The CV to use.
   */
  public setValue(newCV: string) {
    if (this.isValid(newCV)) {
      this.cV = newCV;
      this.currentElement = 0;
    } else {
      throw new NamedError("InvalidCVFormatError", "CV value set to invalid correlation vector");
    }
  }

  public isValid(cv: string) {
    if (cv) {
      return cv.length <= MAX_LENGTH;
    }
    return false;
  }

  /**
   * Gets the app's current CV value.  Initializes it if needed.
   */
  public value(): string {
    if (!this.cV) {
      this.cV = this.initialize();
    }

    return `${this.cV}.${this.currentElement}`;
  }

  public increment(): string {
    if (!this.cV) {
      this.cV = this.initialize();
      return this.value();
    }

    // CVs have a maximum length.  We won't expect to hit it very often.
    if (this.cV.length + 1 + (this.currentElement + 1).toString().length <= MAX_LENGTH) {
      this.currentElement++;
      return this.value();
    }

    throw new NamedError("CVMaximumLengthError", "CV was incremented beyond maximum length");
  }

  public extend(): string {
    if (!this.cV) {
      this.initialize();
      return this.value();
    }

    // CVs have a maximum length.  We won't expect to hit it very often.
    if (this.value().length + 2 <= MAX_LENGTH) {
      this.cV = this.value();
      this.currentElement = 0;

      return this.value();
    }

    throw new NamedError("CVMaximumLengthError", "CV was extended beyond maximum length");
  }

  private initialize() {
    const base64CharSet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    this.cV = "";
    for (let i = 0; i < BASE_LENGTH; i++) {
      // We don't need a cryptographically secure random number here.
      this.cV += base64CharSet.charAt(Math.floor(Math.random() * base64CharSet.length));
    }
    return this.cV;
  }
}

let correlationVector: CorrelationVector | undefined;

function createCorrelationVector(): CorrelationVector {
  const newCorrelationVector = new CorrelationVector();
  const cvBaseCookie = getCookie("cvBase");
  if (cvBaseCookie) {
    newCorrelationVector.setValue(cvBaseCookie);
  }
  return newCorrelationVector;
}

export const getCorrelationVector = (): string => {
  if (!correlationVector) {
    correlationVector = createCorrelationVector();
  }

  return correlationVector.value();
};

export const incrementCorrelationVector = (): string => {
  if (!correlationVector) {
    correlationVector = createCorrelationVector();
  } else {
    correlationVector.increment();
  }

  return correlationVector.value();
};
